import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Axe, ChevronDown, FileText, Link2, Loader, TriangleAlert, X } from 'lucide-react';
import { SkeletonLoader } from '../SkeletonLoader';
import { PlayerSearchField, type PlayerSelection } from '../eventteam/PlayerSearchField';
import { CreatorProfile } from '../CreatorProfile';
import { logbookApiService, LogbookApiError } from '../../services/logbookApi';
import { notesApiService } from '../../services/notesApi';
import { relativeTime } from '../../utils/logbook';
import { cn } from '../../utils/cn';
import type { LogbookCaseItemType } from '../../types/logbook';

/** The record types that can be looked up by player. The rest are linked by their own identifier. */
type LookupType = Extract<LogbookCaseItemType, 'note' | 'warning' | 'ban'>;

interface Candidate {
  itemType: LookupType;
  itemId: string;
  summary: string;
  creator: string;
  createdAt: number;
}

const TYPE_META: Record<LookupType, { label: string; plural: string; badge: string; icon: typeof FileText }> = {
  note: { label: 'Note', plural: 'Notes', badge: 'badge-info', icon: FileText },
  warning: { label: 'Warning', plural: 'Warnings', badge: 'badge-warning', icon: TriangleAlert },
  ban: { label: 'Ban', plural: 'Bans', badge: 'badge-danger', icon: Axe },
};

type ByIdType = Exclude<LogbookCaseItemType, LookupType>;

const BY_ID_LABELS: Record<ByIdType, string> = {
  incident: 'Incident',
  ticket: 'Ticket',
  appeal: 'Appeal',
};

interface LinkRecordModalProps {
  caseId: string;
  /** Players the case already names; the first is offered without anyone having to type a UUID. */
  subjects: { uuid: string; role: string }[];
  /** `type:id` pairs already attached, so nothing is offered that is already on the case. */
  alreadyLinked: Set<string>;
  onClose: () => void;
  onLinked: () => void;
}

/**
 * Attaches the note, warning or ban a case justified.
 *
 * The record is chosen from the player's own moderation history rather than typed in: the
 * identifiers involved are UUIDs, they are not visible from this page, and copying one between two
 * tabs is the kind of step that quietly does not happen. Anything without a per-player list —
 * incidents, tickets, appeals — still accepts its identifier directly, so nothing becomes
 * unreachable.
 */
export const LinkRecordModal = ({ caseId, subjects, alreadyLinked, onClose, onLinked }: LinkRecordModalProps) => {
  const preferred = useMemo(
    () => subjects.find((subject) => subject.role === 'suspect') ?? subjects[0],
    [subjects],
  );

  const [player, setPlayer] = useState<PlayerSelection | null>(
    preferred ? { uuid: preferred.uuid, name: null } : null,
  );
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [partial, setPartial] = useState(false);
  const [types, setTypes] = useState<LookupType[]>(['note', 'warning', 'ban']);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [byIdOpen, setByIdOpen] = useState(false);
  const [byIdType, setByIdType] = useState<ByIdType>('incident');
  const [byId, setById] = useState('');

  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => { if (keyEvent.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!player) { setCandidates(null); setPartial(false); return; }
    let cancelled = false;
    setCandidates(null);
    // One surface being down should still let the others be linked, so each list is settled on its own.
    Promise.allSettled([
      notesApiService.getNotesForPlayer(player.uuid),
      notesApiService.getWarningsForPlayer(player.uuid),
      notesApiService.getBansForUser(player.uuid),
    ]).then(([notes, warnings, bans]) => {
      if (cancelled) return;
      const found: Candidate[] = [];
      if (notes.status === 'fulfilled') {
        for (const entry of notes.value ?? []) {
          found.push({ itemType: 'note', itemId: entry.id, summary: entry.content, creator: entry.creator_id, createdAt: entry.created_at });
        }
      }
      if (warnings.status === 'fulfilled') {
        for (const entry of warnings.value ?? []) {
          found.push({ itemType: 'warning', itemId: entry.id, summary: entry.content, creator: entry.creator_id, createdAt: entry.created_at });
        }
      }
      if (bans.status === 'fulfilled') {
        for (const entry of bans.value ?? []) {
          found.push({ itemType: 'ban', itemId: entry.ban_uuid, summary: entry.reason, creator: entry.banner_email, createdAt: entry.created });
        }
      }
      setPartial([notes, warnings, bans].some((result) => result.status === 'rejected'));
      setCandidates(found.sort((left, right) => right.createdAt - left.createdAt));
    });
    return () => { cancelled = true; };
  }, [player]);

  const matching = useMemo(
    () => (candidates ?? []).filter((entry) => types.includes(entry.itemType) && !alreadyLinked.has(`${entry.itemType}:${entry.itemId}`)),
    [candidates, types, alreadyLinked],
  );

  const link = async (itemType: LogbookCaseItemType, itemId: string) => {
    setSaving(`${itemType}:${itemId}`);
    setError(null);
    try {
      await logbookApiService.linkCase(caseId, itemType, itemId);
      onLinked();
    } catch (caught) {
      setError(caught instanceof LogbookApiError ? caught.message : 'That record could not be linked.');
      setSaving(null);
    }
  };

  const toggle = (type: LookupType) => setTypes((current) => (
    current.includes(type) ? current.filter((entry) => entry !== type) : [...current, type]
  ));

  return (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-xl max-md:max-w-[95vw]">
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Attach a record</h2>
            <p className="modal-subtitle">Link the note, warning or ban this case justified.</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-3">
          {error && (
            <div className="alert alert-danger">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Player</span>
            <PlayerSearchField value={player} onChange={setPlayer} placeholder="Search a player, or paste a UUID" />
          </div>

          {player && (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="eyebrow mr-1">Show</span>
                {(Object.keys(TYPE_META) as LookupType[]).map((type) => {
                  const meta = TYPE_META[type];
                  const Icon = meta.icon;
                  const on = types.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(type)}
                      className={cn('badge', on ? meta.badge : 'badge-neutral opacity-60')}
                    >
                      <Icon className="w-3 h-3" />
                      <span>{meta.plural}</span>
                    </button>
                  );
                })}
              </div>

              {partial && (
                <div className="alert alert-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>Some of this player&rsquo;s records could not be read, so the list below may be short.</span>
                </div>
              )}

              <div className="max-h-72 overflow-y-auto space-y-1">
                {candidates === null ? (
                  Array.from({ length: 4 }, (_, index) => <SkeletonLoader key={index} className="h-14 w-full rounded-lg" />)
                ) : matching.length === 0 ? (
                  <p className="text-sm text-glass-500 py-4 text-center">
                    {candidates.length === 0
                      ? 'This player has no notes, warnings or bans.'
                      : 'Nothing left to attach for the selected types.'}
                  </p>
                ) : matching.map((entry) => {
                  const meta = TYPE_META[entry.itemType];
                  const key = `${entry.itemType}:${entry.itemId}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => link(entry.itemType, entry.itemId)}
                      disabled={saving !== null}
                      className="w-full text-left px-3 py-2 rounded-lg border border-transparent hover:bg-glass-200/[0.06] transition-colors flex items-start gap-2 disabled:opacity-60"
                    >
                      <span className={cn('badge shrink-0 mt-0.5', meta.badge)}>{meta.label}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{entry.summary || 'No reason recorded'}</span>
                        <span className="flex flex-wrap items-center gap-x-2 text-xs text-glass-500 tabular-nums">
                          <CreatorProfile creatorId={entry.creator} inline />
                          <span aria-hidden>&middot;</span>
                          <span>{relativeTime(entry.createdAt)}</span>
                        </span>
                      </span>
                      {saving === key && <Loader className="w-4 h-4 animate-spin shrink-0 mt-1" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Incidents, tickets and appeals have no per-player list to browse, so they keep an id field. */}
          <div className="border-t border-glass-200/10 pt-3">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-glass-400 hover:text-white"
              aria-expanded={byIdOpen}
              onClick={() => setByIdOpen((value) => !value)}
            >
              <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', byIdOpen && 'rotate-180')} />
              Attach an incident, ticket or appeal by id
            </button>

            {byIdOpen && (
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <select
                  value={byIdType}
                  onChange={(event) => setByIdType(event.target.value as ByIdType)}
                  className="glass-select sm:w-36 shrink-0"
                  aria-label="Record type"
                >
                  {(Object.keys(BY_ID_LABELS) as ByIdType[]).map((type) => (
                    <option key={type} value={type}>{BY_ID_LABELS[type]}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={byId}
                  onChange={(event) => setById(event.target.value)}
                  placeholder={`${BY_ID_LABELS[byIdType]} id`}
                  className="form-input flex-1 min-w-0 font-mono text-xs"
                />
                <button
                  type="button"
                  className="glass-button shrink-0"
                  disabled={!byId.trim() || saving !== null}
                  onClick={() => link(byIdType, byId.trim())}
                >
                  <Link2 className="w-4 h-4" />
                  Link
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="glass-button-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
